const COACHMARK_PHRASES = [
    "choose your view",
    "dark and light themes",
    "switching between dark and light",
];

const DIALOG_SELECTOR = "s-dialog, s-popover, [role='dialog']";

const TOUR_SELECTOR = ".shepherd-element, .shepherd-modal-overlay-container, x-guided-tours";

type ClosableElement = HTMLElement & {
    close?: (returnValue?: string) => void;
    open?: boolean;
};

/**
 * First-visit shell coachmarks (theme intro, guided tours) are not certificate dialogs.
 * @param element - Candidate overlay
 * @returns Whether the overlay copy is a shell coachmark
 */
function isShellCoachmark(element: Element): boolean {
    const lightText = element.textContent ?? "";
    const shadowText = element.shadowRoot?.textContent ?? "";
    const text = `${lightText} ${shadowText}`.toLowerCase();

    return COACHMARK_PHRASES.some(phrase => text.includes(phrase));
}

/**
 * Closes a shell overlay without touching the certificate form.
 * @param element - Overlay to close
 */
function closeShellPopup(element: Element): void {
    const closable = element as ClosableElement;

    if (typeof closable.close === "function") {
        closable.close();
        return;
    }

    if ("open" in closable) {
        closable.open = false;
    }

    element.remove();
}

/**
 * Removes theme-intro and tour overlays that Skylab or the AvaApp shell injects.
 * Certificate dialogs stay open because their copy does not match the coachmark phrases.
 * @param doc - Document to scan
 */
export function dismissAviatorShellPopups(doc: Document = document): void {
    const closed = new Set<Element>();
    const closeOnce = (element: Element): void => {
        if (closed.has(element)) {
            return;
        }

        closed.add(element);
        closeShellPopup(element);
    };

    doc.querySelectorAll(TOUR_SELECTOR).forEach(closeOnce);

    doc.querySelectorAll(DIALOG_SELECTOR).forEach(element => {
        if (isShellCoachmark(element)) {
            closeOnce(element);
        }
    });

    Array.from(doc.body?.children ?? []).forEach(child => {
        if (child.id === "root" || child.classList.contains("app-container")) {
            return;
        }

        if (child.querySelector("#root, .app-container")) {
            return;
        }

        if (isShellCoachmark(child)) {
            closeOnce(child);
        }
    });
}

/**
 * Watches for shell coachmarks added after the Aviator page mounts.
 * @param doc - Document to observe
 * @returns Cleanup that disconnects the observer
 */
export function watchAviatorShellPopups(doc: Document = document): () => void {
    dismissAviatorShellPopups(doc);

    const observer = new MutationObserver(() => {
        dismissAviatorShellPopups(doc);
    });

    observer.observe(doc.body, { childList: true, subtree: true });

    return () => {
        observer.disconnect();
    };
}
